import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AuthLayout } from '../components/AuthLayout';

/**
 * Landing page for the signup-confirmation email when the account was
 * created inside the iPhone / Android app.
 *
 * The email link always opens in the phone's browser (Safari / Chrome),
 * because it goes via Supabase's verify endpoint first. Rather than let
 * the customer carry on in the browser (where they'd see the web paywall
 * and get confused), we tell them the email is confirmed and hand them
 * back to the app.
 *
 * `?source=app` is added by app builds from 1.1.1 onwards, which register
 * the `safeandsorted://` URL scheme — for those we auto-open the app and
 * show an "Open the app" button. Older builds (1.1 build 6) don't have
 * the scheme, so we only show the "switch back to the app" instructions.
 */
const APP_SCHEME_URL = 'safeandsorted://email-confirmed';

export function EmailConfirmed() {
  const canOpenApp = (() => {
    try {
      return new URLSearchParams(window.location.search).get('source') === 'app';
    } catch {
      return false;
    }
  })();

  useEffect(() => {
    if (!canOpenApp) return;
    // Try to jump straight back into the app. On iOS Safari this shows
    // an "Open in Safe and Sorted?" prompt; if the app isn't installed
    // nothing happens and the page below stays visible.
    const t = window.setTimeout(() => {
      window.location.href = APP_SCHEME_URL;
    }, 400);
    return () => window.clearTimeout(t);
  }, [canOpenApp]);

  return (
    <AuthLayout title="Email confirmed">
      <h1>Email confirmed ✅</h1>
      <p className="muted">
        Your account is ready. Go back to the <strong>Safe and Sorted</strong> app
        to choose your plan and get started.
      </p>

      {canOpenApp && (
        <a
          href={APP_SCHEME_URL}
          className="btn btn-primary"
          style={{ display: 'block', textAlign: 'center', textDecoration: 'none', marginTop: 16 }}
        >
          Open the Safe and Sorted app
        </a>
      )}

      <p className="muted" style={{ fontSize: 13, marginTop: 16, lineHeight: 1.5 }}>
        {canOpenApp
          ? "If the app doesn't open, switch back to it yourself and tap “I've confirmed my email”."
          : 'Switch back to the app and sign in with the email and password you just chose.'}
      </p>

      <p className="auth-footer">
        Using a computer instead? <Link to="/">Continue on the website</Link>
      </p>
    </AuthLayout>
  );
}
